/**
 * Ingestion Service — trigger and monitor email/calendar ingestion jobs.
 *
 * Calls /api/ingestion/* routes on the ingestion-api Lambda.
 * Auth: Cognito JWT via authService.getIdToken().
 *
 * Note: jobId format is `${uuid}#${timestamp}` — the '#' is URL-unsafe,
 * so status lookups use query param `?jobId=` not path param.
 */

import authService from './authService';
import { API_CONFIG } from '../config/api';

// ─── Types ─────────────────────────────────────────────────────────

export interface IngestionPreview {
  employeeId: string;
  employeeName: string;
  messageCountEstimate: number;
  sampleMessages: Array<{
    subject: string;
    from: string;
    receivedDateTime: string;
  }>;
  warnings: string[];
  cutoffDays: number;
}

export interface IngestionJobStart {
  employeeId: string;
  sourceType?: string;
  jobId?: string;
  jobType?: string;
  status?: 'started' | 'queued' | 'skipped';
  activeJobId?: string | null;
  reason?: string;
  error?: string;
}

export interface IngestionJob {
  jobId: string;
  employeeId: string;
  jobType: 'initial' | 'incremental' | 'continuation';
  sourceType: 'email' | 'calendar';
  status: 'pending' | 'running' | 'completed' | 'completed_partial' | 'failed';
  documentsTotal?: number;
  documentsProcessed?: number;
  vectorsCreated?: number;
  errorsCount?: number;
  startedAt?: string;
  completedAt?: string;
  isPartial: boolean;
}

// ─── HTTP helper ───────────────────────────────────────────────────

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await authService.getIdToken();

  const response = await fetch(`${API_CONFIG.BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.error || `Request failed with status ${response.status}`;
    const error = new Error(message) as any;
    error.errorCode = errorBody.error_code;
    error.status = response.status;
    error.activeJobId = errorBody.activeJobId ?? null;
    throw error;
  }

  return response.json();
}

// ─── Service methods ───────────────────────────────────────────────

/**
 * Preview mailbox size and sample messages before committing to ingestion.
 * Admin/owner only.
 */
export async function previewIngestion(
  employeeId: string,
  cutoffDays?: number
): Promise<IngestionPreview> {
  return apiRequest<IngestionPreview>('/api/ingestion/preview', {
    method: 'POST',
    body: JSON.stringify({ employeeId, cutoffDays }),
  });
}

/**
 * Start ingestion for one or more employees.
 * Admin/owner only. MVP limit: 1 employee per request.
 * Iterates all enabled sources; queued sources return status: 'queued'.
 * Optional sourceTypes overrides which sources to start.
 */
export async function startIngestion(
  employeeIds: string[],
  sourceTypes?: string[]
): Promise<{ jobs: IngestionJobStart[] }> {
  return apiRequest<{ jobs: IngestionJobStart[] }>('/api/ingestion/start', {
    method: 'POST',
    body: JSON.stringify({ employeeIds, ...(sourceTypes ? { sourceTypes } : {}) }),
  });
}

/**
 * Update the enabled sources for an employee.
 * If a new source is added and the connection is active, triggers an initial job.
 */
export async function updateEmployeeSources(
  employeeId: string,
  enabledSources: string[]
): Promise<{ employee: { employeeId: string; enabledSources: string[] }; startedJobs: Array<{ sourceType: string; jobId: string; status: string }> }> {
  return apiRequest(`/api/ingestion/employees/${employeeId}/sources`, {
    method: 'PATCH',
    body: JSON.stringify({ enabledSources }),
  });
}

/**
 * Get status for a specific ingestion job.
 * jobId format is uuid#timestamp — encodeURIComponent escapes the '#' to '%23'.
 * The handler also supports ?jobId= query param as fallback.
 */
export async function getIngestionStatus(jobId: string): Promise<{ job: IngestionJob }> {
  return apiRequest<{ job: IngestionJob }>(
    `/api/ingestion/status/${encodeURIComponent(jobId)}`
  );
}

/**
 * List recent ingestion jobs for the current tenant.
 */
export async function listIngestionJobs(
  limit?: number
): Promise<{ jobs: IngestionJob[] }> {
  const qs = limit ? `?limit=${limit}` : '';
  return apiRequest<{ jobs: IngestionJob[] }>(`/api/ingestion/jobs${qs}`);
}

const ingestionService = {
  previewIngestion,
  startIngestion,
  updateEmployeeSources,
  getIngestionStatus,
  listIngestionJobs,
};

export default ingestionService;
